import React, {useState, useContext, useEffect, useRef} from 'react';
import ExportComments from '../../components/buttons/ExportComments';
import {VendorContext} from '../../context/VendorProvider';
import DeleteModal from '../../components/modals/DeleteModal';
import commentIcon from '../../assets/icons/comment.svg';
import verandaIcon from '../../assets/icons/v-icon.svg';

const CommentsComponent = ({VendorID}) => {
  const {
    comments, getVendorComments, postComment, deleteComment, setComments,
  } = useContext(VendorContext);
  const [commentText, setCommentText] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [commentIdToDelete, setCommentIdToDelete] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const prevVendorIDRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleStatusChange = () => {
    setIsPublic(!isPublic);
  };

  const handleCommentTextChange = (event) => {
    setCommentText(event.target.value);
  };

  const handlePostComment = async () => {
    const newComment = {
      comment: commentText, public: isPublic,
    };
    await postComment(VendorID, newComment);
    setCommentText('');
    setIsPublic(false);
  };

  const handleDeleteComment = (commentId) => {
    setCommentIdToDelete(commentId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirmation = async (commentId) => {
    await deleteComment(VendorID, commentId);
    setShowDeleteModal(false);
    const updatedComments = comments.filter((comment) => comment.id !== commentId);
    setComments(updatedComments);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (VendorID && commentText.trim()) {
        handlePostComment();
      }
    } else if (event.key === 'Enter' && event.shiftKey) {
      event.preventDefault();
      const textarea = event.target;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      setCommentText(commentText.substring(0, start) + '\n' + commentText.substring(end));
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 1;
      }, 0);
    }
  };

  useEffect(() => {
    const fetchComments = async () => {
      if (VendorID) {
        await getVendorComments(VendorID);
      }
    };
    if (prevVendorIDRef.current !== VendorID) {
      fetchComments();
      prevVendorIDRef.current = VendorID;
    }
  }, [VendorID, getVendorComments]);

  return (<div
      className="comments-component mt-2 flex flex-col rounded-lg bg-white px-6 dark:bg-stone-900"
      style={{height: '550px'}}
    >
      {initialLoading ? (<div
          className="mb-2 mt-8 flex h-[58%] items-center justify-center rounded-md bg-gray-100 text-center text-gray-400 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300">
          <div className="mr-2.5 inline-block h-3 w-3 animate-spin rounded-full border-b-2 border-gray-300"/>
        </div>) : (<>
          <div className="mb-6">
            <label className="switch">
              <input
                className="sr-only"
                type="checkbox"
                name="status"
                checked={isPublic}
                onChange={handleStatusChange}
              />
              <div
                className={`slider round ${isPublic ? 'bg-blue-400' : 'bg-gray-200'}`}
              ></div>
            </label>
          </div>
          <div
            className="mb-2 flex-grow overflow-y-auto rounded-md border border-gray-200 dark:border-stone-700"
            style={{maxHeight: 'calc(460px - 6rem)'}}
          >
            {comments.length ? (comments.map((comment) => (<div
                  key={comment.id}
                  className="mb-4 border-b border-gray-200 pb-4 dark:border-gray-600"
                >
                  <div className="mb-1 flex items-center justify-between px-2 dark:text-gray-300">
                    <div>
                      <span
                        className={`font-semibold ${comment.user ? 'text-gray-700 dark:text-indigo-400 dark:hover:text-indigo-500' : 'text-red-500'}`}
                      >
                        {comment.user ? comment.user : 'Deleted User'}:
                      </span>
                      <span className="ml-2">
                        {comment.comment.replace(/\\/g, '')}
                      </span>
                    </div>
                  </div>
                  <div className="mb-2 flex items-center justify-between px-2 dark:text-gray-300">
                    <span className="text-sm text-gray-600">
                      {comment.public ? (<>
                          <img
                            src={verandaIcon}
                            alt="Export"
                            className="mb-1 inline-block h-5 w-5"
                          />
                          <p className="inline-block">Public</p>
                        </>) : (<>
                          <svg
                            className="bi bi-incognito mb-0.5 inline-block"
                            fill="currentColor"
                            height="14"
                            viewBox="0 0 16 16"
                            width="14"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="m4.736 1.968-.892 3.269-.014.058C2.113 5.568 1 6.006 1 6.5 1 7.328 4.134 8 8 8s7-.672 7-1.5c0-.494-1.113-.932-2.83-1.205a1.032 1.032 0 0 0-.014-.058l-.892-3.27c-.146-.533-.698-.849-1.239-.734C9.411 1.363 8.62 1.5 8 1.5c-.62 0-1.411-.136-2.025-.267-.541-.115-1.093.2-1.239.735Zm.015 3.867a.25.25 0 0 1 .274-.224c.9.092 1.91.143 2.975.143a29.58 29.58 0 0 0 2.975-.143.25.25 0 0 1 .05.498c-.918.093-1.944.145-3.025.145s-2.107-.052-3.025-.145a.25.25 0 0 1-.224-.274ZM3.5 10h2a.5.5 0 0 1 .5.5v1a1.5 1.5 0 0 1-3 0v-1a.5.5 0 0 1 .5-.5Zm-1.5.5c0-.175.03-.344.085-.5H2a.5.5 0 0 1 0-1h3.5a1.5 1.5 0 0 1 1.488 1.312 3.5 3.5 0 0 1 2.024 0A1.5 1.5 0 0 1 10.5 9H14a.5.5 0 0 1 0 1h-.085c.055.156.085.325.085.5v1a2.5 2.5 0 0 1-5 0v-.14l-.21-.07a2.5 2.5 0 0 0-1.58 0l-.21.07v.14a2.5 2.5 0 0 1-5 0v-1Zm8.5-.5h2a.5.5 0 0 1 .5.5v1a1.5 1.5 0 0 1-3 0v-1a.5.5 0 0 1 .5-.5Z"
                              fill="currentColor"
                              className="dark:text-indigo-400"
                              fillRule="evenodd"
                            />
                          </svg>
                          <p className="inline-block pl-1">Private</p>
                        </>)}
                    </span>
                    <div className="flex items-center">
                      <div className="mr-2 text-sm text-gray-500">
                        {comment.dateTime}
                      </div>
                      <button
                        className="danger-small"
                        onClick={() => handleDeleteComment(comment.id)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="mb-0.5 mr-1 inline-block h-4 w-4"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0

01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                          />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>))) : (<div
                className="flex h-full items-center justify-center rounded-md border-2 border-gray-100 bg-gray-100 text-center text-gray-400 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300">
                No comments
              </div>)}
          </div>
        </>)}
      <div className="flex flex-col space-y-3">
        <label
          className={`comment-switch ${isPublic ? 'bg-blue-400 dark:bg-indigo-500' : 'bg-gray-200'}`}
        >
          <input
            className="hidden"
            type="checkbox"
            name="status"
            checked={isPublic}
            onChange={handleStatusChange}
            disabled={!VendorID}
          />
          <span
            className={`block h-4 w-4 rounded-full bg-white shadow-inner transition-transform duration-200 ease-in-out ${isPublic ? 'translate-x-14 transform' : ''} ${!VendorID ? 'cursor-not-allowed opacity-50' : ''}`}
          ></span>
          <span
            className={`text-xs ${isPublic ? 'text-white' : 'text-gray-400 dark:text-gray-600'} ${!VendorID ? 'cursor-not-allowed opacity-50' : ''}`}
            style={{userSelect: 'none'}}
          >
            {isPublic ? 'Public' : 'Private'}
          </span>
        </label>
        <div className="relative">
          <textarea
            className={`h-24 w-full rounded-lg border p-2 text-gray-700 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300 ${!VendorID ? 'cursor-not-allowed opacity-50' : ''}`}
            value={commentText}
            onChange={VendorID ? handleCommentTextChange : null}
            onKeyDown={handleKeyDown}
            placeholder={isPublic ? 'Write a public comment...' : 'Write a private comment...'}
            maxLength={5120}
            disabled={!VendorID}
          />
          <div className="absolute bottom-2 right-2 text-sm text-gray-400 dark:text-gray-300">
            {commentText.length}/{5120}
          </div>
        </div>
        <div className="flex w-full flex-col items-center space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
          <button
            className={`primary mt-2 ${!VendorID || !commentText.trim() ? 'cursor-not-allowed opacity-50' : ''}`}
            onClick={VendorID && commentText.trim() ? handlePostComment : null}
            disabled={!VendorID || !commentText.trim()}
          >
            <img
              src={commentIcon}
              alt="Comment"
              className="mr-1.5 inline-block h-4 w-4"
            />
            Comment
          </button>
          {VendorID ? (<ExportComments excelData={comments} fileName="VendorComments"/>) : ('')}
        </div>
      </div>
      {showDeleteModal && (<DeleteModal
          title="Are you sure?"
          onCancel={() => setShowDeleteModal(false)}
          onDelete={() => handleDeleteConfirmation(commentIdToDelete)}
        ></DeleteModal>)}
    </div>);
};

export default CommentsComponent;
